/** Keep in sync with `api/client.ts` — do not import client here (Vitest mocks break named exports). */
export const VICIDIAL_API_BASE_URL = String(
  import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api',
).replace(/\/$/, '')

export const VICIDIAL_AGENT_LOGIN_URL =
  import.meta.env.VITE_VICIDIAL_AGENT_LOGIN_URL ?? 'https://pbx.servicedesk.com.ph/agc/vicidial.php'

export const VICIDIAL_AGENT_LOGOUT_URL =
  import.meta.env.VITE_VICIDIAL_AGENT_LOGOUT_URL ??
  'https://pbx.servicedesk.com.ph/agc/vicidial.php?ACTION=LOGOUT'

/** Hidden / Phone-widget iframe that hosts the Vicidial agent session (CRM pattern). */
export const VICIDIAL_AGENT_FRAME_ID = 'atlas-vicidial-agent-frame'

let agentPopup: Window | null = null

/** How long to wait for Agent API live status after bridge load (ms). */
export const VICIDIAL_STATUS_POLL_TIMEOUT_MS = 45_000

export const VICIDIAL_STATUS_POLL_INTERVAL_MS = 2_000

/** Cap each status poll so a slow API cannot freeze Connecting forever. */
export const VICIDIAL_STATUS_POLL_REQUEST_MS = 8_000

/** When true, ALICE Call out menu/actions are shown. Wave A default: hidden. */
export const ALICE_CALL_OUT_ENABLED =
  String(import.meta.env.VITE_ALICE_CALL_OUT_ENABLED ?? 'false').toLowerCase() === 'true'

export const VICIDIAL_REASON_MESSAGES: Record<string, string> = {
  NO_AGENT_MAPPING: "Your account isn't set up for Vicidial yet — contact your admin.",
  DUPLICATE_AGENT_MAPPING:
    'Your Vicidial agent username is assigned to another ActionLabs user — contact your admin.',
  AGENT_UNREACHABLE:
    'Agent session not ready — Connect phone and wait for Phone connected, or use Show agent screen.',
  DIALER_UNAVAILABLE:
    'Vicidial dialing is currently unavailable — check API credentials (.env) or ask your admin.',
  INVALID_PHONE_FORMAT:
    'Enter a valid PH mobile (9XXXXXXXXX) or landline number. Do not include +63 or a leading 0.',
  CREDENTIALS_INCOMPLETE:
    'Vicidial login details are incomplete — ask an admin to set agent and phone credentials in User Management.',
}

export function vicidialFailureMessage(reason?: string, fallback?: string): string {
  if (fallback && fallback.trim() !== '') {
    return fallback.trim()
  }
  return (
    (reason && VICIDIAL_REASON_MESSAGES[reason]) ||
    'Vicidial dialing is currently unavailable — contact your admin.'
  )
}

export function normalizePhilippinesDialValue(phoneNumber: string): string | null {
  let digits = phoneNumber.replace(/\D+/g, '')
  if (!digits) return null
  if (digits.startsWith('63') && digits.length >= 11) {
    digits = digits.slice(2)
  }
  if (digits.startsWith('0')) {
    digits = digits.slice(1)
  }
  if (digits.length === 10 && digits.startsWith('9')) return digits
  if (digits.length === 9) return digits
  return null
}

/**
 * Always build the bridge against VITE_API_URL (same host as apiClient).
 * Never trust APP_URL alone — that pointed the iframe at the wrong server.
 */
export function resolveVicidialBridgeUrl(data: { path?: string; url?: string }): string | null {
  const base = VICIDIAL_API_BASE_URL
  let relative = (data.path || '').replace(/^\//, '')

  if (!relative && data.url) {
    const marker = 'support/vicidial/agent-login-bridge/'
    const idx = data.url.indexOf(marker)
    if (idx >= 0) {
      relative = data.url.slice(idx)
    }
  }

  if (!relative || !relative.includes('support/vicidial/agent-login-bridge/')) {
    return null
  }

  return `${base}/${relative}`
}

/** Off-screen but non-zero size — Chrome denies getUserMedia in 0×0 / 2×2 iframes. */
export const VICIDIAL_AGENT_FRAME_HIDDEN_STYLE =
  'position:fixed;right:0;bottom:0;width:400px;height:300px;opacity:0;border:0;z-index:1;pointer-events:none;clip-path:inset(100%)'

export const VICIDIAL_AGENT_FRAME_VISIBLE_STYLE =
  'position:fixed;left:50%;top:56px;transform:translateX(-50%);width:min(1100px,calc(100vw - 32px));height:calc(100dvh - 72px);opacity:1;border:1px solid #CBD5E0;border-radius:8px;z-index:1601;background:#fff;pointer-events:auto'

export function ensureAgentFrame(): HTMLIFrameElement {
  let iframe = document.getElementById(VICIDIAL_AGENT_FRAME_ID) as HTMLIFrameElement | null
  if (!iframe) {
    iframe = document.createElement('iframe')
    iframe.id = VICIDIAL_AGENT_FRAME_ID
    iframe.title = 'Vicidial agent session'
    // Cross-origin VICIphone needs explicit mic/autoplay delegation from ATLAS.
    iframe.allow = 'microphone *; autoplay *; camera *'
    iframe.setAttribute('allow', 'microphone *; autoplay *; camera *')
    iframe.setAttribute('allowfullscreen', 'true')
    iframe.style.cssText = VICIDIAL_AGENT_FRAME_HIDDEN_STYLE
    document.body.appendChild(iframe)
  } else {
    iframe.allow = 'microphone *; autoplay *; camera *'
    iframe.setAttribute('allow', 'microphone *; autoplay *; camera *')
  }
  return iframe
}

/**
 * Prime mic permission on a user gesture (parent page). Cross-origin Vicidial
 * may still prompt for its own host — show the agent screen so that dialog can appear.
 */
export async function requestMicrophoneAccess(): Promise<{ ok: boolean; message?: string }> {
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return {
      ok: false,
      message: 'Browser phone audio requires HTTPS or localhost. Open VICIDIAL directly over HTTPS, or use a secure ATLAS address.',
    }
  }
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      message: 'This browser does not support WebRTC microphone access.',
    }
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    stream.getTracks().forEach((track) => track.stop())
    return { ok: true }
  } catch (error) {
    const name = error instanceof DOMException ? error.name : 'Error'
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return {
        ok: false,
        message:
          'Microphone permission denied — allow mic for ATLAS and for the Vicidial host when prompted, then try again.',
      }
    }
    return {
      ok: false,
      message: 'Could not access the microphone. Check browser settings and try again.',
    }
  }
}

/** Navigate the agent iframe to Vicidial logout (best-effort session teardown). */
export function teardownVicidialSession(logoutUrl?: string | null): void {
  const url = (logoutUrl && logoutUrl.trim()) || VICIDIAL_AGENT_LOGOUT_URL
  const iframe = document.getElementById(VICIDIAL_AGENT_FRAME_ID) as HTMLIFrameElement | null
  if (iframe) {
    try {
      iframe.src = url
    } catch {
      /* ignore */
    }
  }
  try {
    const popup = agentPopup
    if (popup && !popup.closed) {
      try {
        popup.location.href = url
      } catch {
        try {
          popup.close()
        } catch {
          /* ignore */
        }
      }
    }
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent('atlas-vicidial-session-cleared'))
}

function axiosErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { status?: number; data?: { message?: string; data?: { reason?: string } } } })
      .response
    const apiMessage = response?.data?.message
    const reason = response?.data?.data?.reason
    if (apiMessage || reason) {
      return vicidialFailureMessage(reason, apiMessage)
    }
    if (response?.status === 404) {
      return 'Vicidial login API route not found — restart/rebuild the backend route cache, then try again.'
    }
    if (response?.status === 401) {
      return 'Session expired — log in to ATLAS again, then Connect phone.'
    }
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'Could not start the in-page Vicidial session. Confirm credentials in User Management, then try Connect phone again.'
}

export type VicidialLoginLinkResult = {
  path?: string
  url?: string
  logout_url?: string
  message?: string
  reason?: string
}

/**
 * Load the one-time ATLAS login bridge into a hidden iframe (Vicidial CRM pattern).
 * Popup is recovery-only when `openPopupRecovery` is true.
 */
export async function connectVicidialInPage(
  requestLoginLink: () => Promise<VicidialLoginLinkResult>,
  options?: { openPopupRecovery?: boolean; signal?: AbortSignal },
): Promise<{ ok: boolean; message?: string; bridgeUrl?: string; logoutUrl?: string }> {
  try {
    const result = await requestLoginLink()
    if (options?.signal?.aborted) return { ok: false }
    const bridgeUrl = resolveVicidialBridgeUrl(result)
    if (!bridgeUrl) {
      return {
        ok: false,
        message:
          result.message ||
          'Could not build Vicidial login URL. Check VITE_API_URL matches your running API (WAMP public/api).',
      }
    }

    const logoutUrl = result.logout_url || VICIDIAL_AGENT_LOGOUT_URL
    const iframe = ensureAgentFrame()
    iframe.src = bridgeUrl

    if (options?.openPopupRecovery) {
      const agentWin = window.open(
        bridgeUrl,
        'atlas_vicidial_agent',
        'popup=yes,width=1100,height=800,resizable=yes,scrollbars=yes',
      )
      agentPopup = agentWin
      if (agentWin) {
        try {
          agentWin.focus()
        } catch {
          /* ignore */
        }
      }
    }

    window.dispatchEvent(new CustomEvent('atlas-vicidial-frame-connected', { detail: { bridgeUrl } }))
    return {
      ok: true,
      bridgeUrl,
      logoutUrl,
    }
  } catch (error) {
    return {
      ok: false,
      message: axiosErrorMessage(error),
    }
  }
}

/**
 * Poll agent-status until live or timeout. Returns true when Vicidial session is dialable.
 * Each fetch is hard-capped so a hung axios call cannot leave the UI on Connecting.
 */
export async function waitForVicidialAgentLive(
  fetchStatus: () => Promise<{ live: boolean; logoutUrl?: string | null; agentState?: string | null }>,
  options?: {
    timeoutMs?: number
    intervalMs?: number
    requestTimeoutMs?: number
    signal?: AbortSignal
  },
): Promise<{ live: boolean; logoutUrl?: string | null; agentState?: string | null; timedOut?: boolean; aborted?: boolean }> {
  const timeoutMs = options?.timeoutMs ?? VICIDIAL_STATUS_POLL_TIMEOUT_MS
  const intervalMs = options?.intervalMs ?? VICIDIAL_STATUS_POLL_INTERVAL_MS
  const requestTimeoutMs = options?.requestTimeoutMs ?? VICIDIAL_STATUS_POLL_REQUEST_MS
  const deadline = Date.now() + timeoutMs
  let lastLogout: string | null | undefined

  while (Date.now() < deadline) {
    if (options?.signal?.aborted) {
      return { live: false, logoutUrl: lastLogout, aborted: true }
    }

    const remaining = deadline - Date.now()
    const perAttempt = Math.max(1, Math.min(requestTimeoutMs, remaining))
    let timer: ReturnType<typeof setTimeout> | undefined
    let onAbort: (() => void) | undefined

    try {
      const status = await Promise.race([
        fetchStatus(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('vicidial-status-poll-timeout')), perAttempt)
          onAbort = () => reject(new Error('vicidial-status-poll-aborted'))
          options?.signal?.addEventListener('abort', onAbort, { once: true })
        }),
      ])
      if (options?.signal?.aborted) {
        return { live: false, logoutUrl: lastLogout, aborted: true }
      }
      lastLogout = status.logoutUrl
      if (status.live) {
        return { live: true, logoutUrl: status.logoutUrl, agentState: status.agentState }
      }
    } catch {
      /* keep polling through timeouts / network errors */
    } finally {
      if (timer !== undefined) clearTimeout(timer)
      if (onAbort) options?.signal?.removeEventListener('abort', onAbort)
    }

    if (options?.signal?.aborted) {
      return { live: false, logoutUrl: lastLogout, aborted: true }
    }

    const sleepMs = Math.min(intervalMs, Math.max(0, deadline - Date.now()))
    if (sleepMs > 0) {
      await new Promise<void>((resolve) => {
        const finish = () => {
          clearTimeout(sleepTimer)
          options?.signal?.removeEventListener('abort', finish)
          resolve()
        }
        const sleepTimer = setTimeout(finish, sleepMs)
        options?.signal?.addEventListener('abort', finish, { once: true })
      })
    }
  }

  return { live: false, logoutUrl: lastLogout, timedOut: true }
}

/** @deprecated Prefer connectVicidialInPage */
export async function openVicidialAgentSession(
  requestLoginLink: () => Promise<VicidialLoginLinkResult>,
): Promise<{ ok: boolean; message?: string }> {
  return connectVicidialInPage(requestLoginLink)
}
